// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this handler to pass proper expand with getVaraints call
 */

import { getIsWebCrawler } from "@insite/client-framework/Components/ContentItemStore";
import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getProductByPath } from "@insite/client-framework/Services/ProductServiceV2";
import { recentlyViewedGetProductCollectionParameter } from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductByPath from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    LoadProductIfNeeded,
    LoadRealTimeInventory,
    RequestVariantChildrenFromApi,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import loadRegionalInventory from "@mcmc/App/Handlers/CommonHandlers/LoadRegionalInventory";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getIsEvent } from "@mcmc/App/Store/Selectors/ProductDetailsSelectors";

export const CustomRequestVariantChildrenFromApi: typeof RequestVariantChildrenFromApi = async props => {
    if (!props.product?.variantTraits || props.product.variantTraits.length === 0) {
        return;
    }

    props.variantChildren = getVariantChildrenDataView(props.getState(), props.product.id).value;
    if (!props.variantChildren) {
        props.variantChildren = await executeAwaitableHandlerChain(
            loadVariantChildren as any,
            {
                productId: props.product.id,
                // <xng-change>
                expand: ["properties"],
                // </xng-change>
            } as any,
            props,
        );
    }
};

export const LoadRegionalInventory: typeof LoadRealTimeInventory = props => {
    if (!props.product || getIsEvent(props.getState() as McMcApplicationState, props.product) || getIsWebCrawler()) {
        return;
    }

    props.dispatch(
        loadRegionalInventory({
            productIds: [props.product.id, ...(props.variantChildren?.map(o => o.id) ?? [])],
        }),
    );
};

export const CustomLoadProductIfNeeded: typeof LoadProductIfNeeded = async props => {
    // if variantTypeId is not null this has variantTraits, and we need those loaded fully for this handler chain to work correctly
    // if product is discontinued then it may have replacement product, and we wait for it to be loaded
    if (!props.product || props.product.variantTypeId || props.product.isDiscontinued) {
        props.product = await executeAwaitableHandlerChain(
            loadProductByPath,
            { ...props.parameter, addToRecentlyViewed: true },
            props,
        );
    } else if (
        !props.product.detail ||
        !props.product.content ||
        !props.product.images ||
        !props.product.documents ||
        !props.product.specifications ||
        !props.product.attributeTypes ||
        !props.product.variantTraits
    ) {
        // we don't want to wait for this if we already partially have the product, because then we can't show the partially loaded product page
        props.dispatch(
            loadProductByPath({
                ...props.parameter,
                addToRecentlyViewed: true,
                onComplete: productProps => {
                    const configuration = productProps?.apiResult?.detail?.configuration;
                    if (configuration) {
                        props.dispatch({
                            type: "Pages/ProductDetails/InitConfigurationSelection",
                            configuration,
                        });
                    }

                    props.dispatch({
                        type: "Data/Products/ReplaceDataView",
                        parameter: recentlyViewedGetProductCollectionParameter,
                        dataView: {},
                    });
                },
            }),
        );
    } else {
        getProductByPath({
            ...props.parameter,
            addToRecentlyViewed: true,
            expand: [
                "detail",
                "specifications",
                "content",
                "images",
                "documents",
                "attributes",
                "variantTraits",
                "properties",
            ],
            includeAttributes: ["includeOnProduct"],
        }).then(result => {
            props.dispatch({
                type: "Data/Products/CompleteLoadProduct",
                model: result,
            });
        });
    }
};

replaceInChain(chain, RequestVariantChildrenFromApi, CustomRequestVariantChildrenFromApi);
addToChainAfter(chain, LoadRealTimeInventory, LoadRegionalInventory);
replaceInChain(chain, LoadProductIfNeeded, CustomLoadProductIfNeeded);
